import { Node, Element, Text, Point, Path, Ancestor } from 'slate';
import DiffMatchPatch from 'diff-match-patch';
import { CustomElement, ListElement, ListItemElement, ReferenceTableElement } from 'slate/types/slate';
const dmp = new DiffMatchPatch();

type TableCell = { rawData?: { w?: string } };

interface ReferenceData {
  superId?: string;
  selectionContent?: TableCell[][];
}

export interface DiffSource {
  content: unknown[];
  references?: unknown[];
}

type SerializedTextNode = { type: 'text'; text: (string | undefined)[] };
type SerializedListNode = { type: 'bulleted-list' | 'numbered-list' | 'list-item' | 'list'; data: string[] };
type SerializedTableNode = { type: 'table'; data?: TableCell[][] };

export type SerializedNode = SerializedTextNode | SerializedListNode | SerializedTableNode;
export type SerializedResult = Record<string, SerializedNode>;

export type Diff = [number, string | undefined];
export type DiffTree = Diff | DiffTree[];
export type ComputedDiff = DiffTree[] | undefined;

export type DiffNodeState = 'missing' | 'changed' | 'added';

export interface DiffEntry {
  type: SerializedNode['type'] | 'page';
  state?: DiffNodeState;
  computedDiff?: ComputedDiff;
  offset?: number;
}

export interface DiffState {
  left: Record<string, DiffEntry>;
  right: Record<string, DiffEntry>;
  leftSource: SerializedResult;
  rightSource: SerializedResult;
}

export interface DiffDecoration {
  anchor?: Point;
  focus?: Point;
  highlight?: string;
}

export const serialize = ({ content, references }: DiffSource): SerializedResult => {
  const nodeGenerator = Node.nodes({ children: content } as Ancestor);
  const result: SerializedResult = {};
  for (const [node] of nodeGenerator) {
    if (!Element.isElement(node)) {
      continue;
    }
    let serialized: SerializedNode | undefined;
    const nodeType: string = node.type;
    switch (nodeType) {
      case 'heading-one':
      case 'heading-two':
      case 'heading-three':
      case 'paragraph':
      case 'mention':
      case 'custom-formatting':
        serialized = serializeText(node);
        break;
      case 'reference-table':
        serialized = serializeReferenceTable(node as ReferenceTableElement, references);
        break;
      case 'numbered-list':
      case 'bulleted-list':
        serialized = serializeList(node as ListElement);
        break;
      case 'list-item':
        serialized = serializeListItem(node as ListItemElement);
        break;
      default:
    }
    if (serialized) {
      result[getNodeId(node)] = serialized;
    }
  }
  return result;
};

export const getNodeId = (node: Node): string => {
  const id = Element.isElement(node) ? (node.type === 'reference-table' ? node.referenceDataSuperId : node.uuid) : undefined;
  return id as string;
};

const serializeText = (node: CustomElement): SerializedTextNode => {
  return { type: 'text', text: node.children.map((child) => (Text.isText(child) ? child.text : undefined)) };
};

const serializeList = (node: ListElement): SerializedListNode => {
  return {
    type: node.type,
    data: node.children.map((li) => (Element.isElement(li) ? li.children.map((c) => (Text.isText(c) ? c.text : '') || '').join('') : '')),
  };
};
const serializeListItem = (node: ListItemElement): SerializedListNode => {
  return {
    type: 'list-item',
    data: node.children.map((c) => (Text.isText(c) ? c.text : '') || ''),
  };
};

const serializeReferenceTable = (node: ReferenceTableElement, references?: unknown[]): SerializedTableNode => {
  const match = references?.find((ref): ref is ReferenceData => (ref as ReferenceData).superId === node.referenceDataSuperId);
  return { type: 'table', data: match?.selectionContent };
};

const flattenText = (textObj: SerializedTextNode): string => {
  return textObj.text.join('');
};

/**
 * Compares sections, and returns what has happened with nodes in the left document, compared to the right.
 */
const compareDocuments = (documentL: SerializedResult, documentR: SerializedResult): Record<string, DiffEntry> => {
  const diff: Record<string, DiffEntry> = {};
  Object.entries(documentL).forEach(([key, val]) => {
    if (!documentR[key] || documentR[key].type !== val.type) {
      diff[key] = { type: val.type, state: 'missing', computedDiff: missingDiff(val) };
    } else {
      diff[key] = { type: val.type, state: 'changed', computedDiff: complexDiff(val, documentR[key]) };
    }
  });
  Object.entries(documentR).forEach(([key, val]) => {
    if (!documentL[key]) {
      diff[key] = { type: val.type, state: 'added', computedDiff: addedDiff(val) };
    }
  });
  return diff;
};

const complexDiff = (val1: SerializedNode, val2: SerializedNode): ComputedDiff => {
  if (val1.type !== val2.type) {
    return;
  }
  switch (val1.type) {
    case 'text':
      return compareText(val1, val2 as SerializedTextNode);
    case 'list':
      return compareList(val1, val2 as SerializedListNode);
    case 'list-item':
      return compareListItem(val1, val2 as SerializedListNode);
    case 'table':
      return compareTable(val1, val2 as SerializedTableNode);
    default:
      return undefined;
  }
};

const compareListItem = (val1: SerializedListNode, val2: SerializedListNode): Diff[] => {
  const computedDiff = dmp.diff_main(val1.data.join(''), val2.data.join(''));
  dmp.diff_cleanupSemantic(computedDiff);
  return computedDiff;
};

const compareList = (val1: SerializedListNode, val2: SerializedListNode): Diff[] => {
  const diffs: Diff[] = [];
  const maxLength = Math.max(val1.data.length, val2.data.length);

  for (let i = 0; i < maxLength; i++) {
    const item1 = val1.data[i] || '';
    const item2 = val2.data[i] || '';

    const diff = dmp.diff_main(item1, item2);
    dmp.diff_cleanupSemantic(diff);
    diffs.push(diff[0]);
  }

  return diffs;
};

const compareText = (val1: SerializedTextNode, val2: SerializedTextNode): Diff[] => {
  const computedDiff = dmp.diff_main(flattenText(val1), flattenText(val2));
  dmp.diff_cleanupSemantic(computedDiff);
  return computedDiff;
};

const compareTable = (val1: SerializedTableNode, val2: SerializedTableNode): DiffTree[] => {
  if (!val1?.data || !val2?.data) {
    return [];
  }

  // TODO: Mark left as deleted when table shrinks
  const diffs = val1.data.map((row, rowIdx) =>
    row.map((col, colIdx): Diff[] => {
      const cell1 = col.rawData?.w;
      const cell2 = val2.data?.[rowIdx]?.[colIdx]?.rawData?.w;
      if (cell1 && cell2) {
        const diff = dmp.diff_main(cell1, cell2);
        dmp.diff_cleanupSemantic(diff);
        return diff;
      } else if (cell1 === undefined && cell2) {
        return [[-1, cell2]];
      } else if (cell2 === undefined && cell1) {
        return [[1, cell1]];
      } else {
        return [[0, '']];
      }
    })
  );
  return diffs;
};

const missingDiff = (val: SerializedNode): ComputedDiff => {
  switch (val.type) {
    case 'text':
      return [[-1, val.text.join('')]];
    case 'list':
    case 'list-item':
      return [[-1, val.data.join('')]];
    case 'table':
      return val.data?.map((row) => row.map((col): Diff[] => [[-1, col.rawData?.w]]));
    default:
      return undefined;
  }
};

const addedDiff = (val: SerializedNode): ComputedDiff => {
  switch (val.type) {
    case 'text':
      return [[1, val.text.join('')]];
    case 'table':
      return val.data?.map((row) => row.map((col): Diff[] => [[1, col.rawData?.w]]));
    case 'list':
    case 'list-item':
      return [[1, val.data.join('')]];
    default:
      return undefined;
  }
};

type PageNode = { uuid: string };

/**
 * Calculates the offset between two pages, returns empty if no matching pages.
 * Returns a size-2 array: ['left' | 'right', offset].
 */
const closestMatch = (left: PageNode[], right: PageNode[], a: number, b: number): ['left' | 'right', number] | [] => {
  const leftPage = left[a];
  const rightPage = right[b];
  for (let counter = 0; a < left.length || b < right.length; a++, b++, counter++) {
    if (a < left.length && left[a].uuid === rightPage.uuid) {
      return ['left', counter];
    }

    if (b < right.length && right[b].uuid === leftPage.uuid) {
      return ['right', counter];
    }
  }
  return [];
};

/**
 * Calculates the offset of pages between sections.
 */
const pageDiff = (left: PageNode[], right: PageNode[]): { left: Record<string, DiffEntry>; right: Record<string, DiffEntry> } => {
  const diff: { left: Record<string, DiffEntry>; right: Record<string, DiffEntry> } = { left: {}, right: {} };
  for (let a = 0, b = 0; a < left.length && b < right.length; a++, b++) {
    if (left[a].uuid === right[b].uuid) {
      continue;
    }

    const match = closestMatch(left, right, a, b);
    if (match.length === 0) {
      continue;
    }

    const [side, offset] = match;
    if (side === 'left') {
      diff.right[right[b].uuid] = { type: 'page', offset };
      a += offset;
    } else {
      diff.left[left[a].uuid] = { type: 'page', offset };
      b += offset;
    }
  }
  return diff;
};

export const generateDiffState = (left: DiffSource, right: DiffSource): DiffState => {
  const resultL = serialize(left);
  const resultR = serialize(right);
  const extra = pageDiff(left.content as PageNode[], right.content as PageNode[]);
  return {
    left: Object.assign({}, compareDocuments(resultL, resultR), extra.left),
    right: Object.assign({}, compareDocuments(resultR, resultL), extra.right),
    leftSource: resultL,
    rightSource: resultR,
  };
};

export const leafsInRange = (rootPath: Path, parent: Node, from: number, to: number, side: string, _change?: DiffTree): DiffDecoration => {
  let counter = 0;
  let anchor: Point | undefined;
  let focus: Point | undefined;

  for (const [node, path] of Node.descendants(parent)) {
    if (!Text.isText(node) || !node.text) {
      continue;
    }
    const text = node.text;
    if (counter <= from && from < counter + text.length) {
      anchor = { path: [...rootPath, ...path], offset: from - counter };
    }
    if (counter <= to && to <= counter + text.length) {
      focus = { path: [...rootPath, ...path], offset: to - counter };
    }
    counter += text.length;
  }

  return {
    anchor,
    focus,
    highlight: getDiffClass(side),
  };
};

export const getDiffClass = (side: string): string => {
  return side === 'left' ? 'deleted' : 'added';
};
